import { useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@workspace/amarmeds-ds/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@workspace/amarmeds-ds/components/ui/card";

import { api, ApiError } from "../api";
import { prepareImage } from "./images";
import type { ImportResult, ProductKey, StoreMeta } from "./types";
import { baseName, zipEntries, zipRead, type ZipEntry } from "./zip";

// Bulk upload in two steps: the CSV makes or updates the products (checked
// first, then imported), then a ZIP of photos is matched to them by file name
// and each photo is resized here and uploaded on its own.

const PARALLEL = 4;

const errorText = (err: unknown) => (err instanceof ApiError ? err.code.replace(/_/g, " ") : err instanceof Error ? err.message : "Something went wrong.");

export function BulkUpload({ meta }: { meta: StoreMeta | null }) {
  return (
    <div className="space-y-4">
      <CsvStep meta={meta} />
      <PhotoStep />
    </div>
  );
}

function CsvStep({ meta }: { meta: StoreMeta | null }) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [csv, setCsv] = useState<{ name: string; text: string } | null>(null);
  const [check, setCheck] = useState<ImportResult | null>(null);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Words changed in names and descriptions on import (Arogga is AmarMeds' partner: its name becomes ours).
  const [replace, setReplace] = useState<{ from: string; to: string }[]>([{ from: "Arogga", to: "AmarMeds" }]);
  const usable = replace.filter((r) => r.from.trim().length >= 2);

  async function run(dryRun: boolean, text = csv?.text) {
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api<ImportResult>("/ops/store/import", { body: { csv: text, dryRun, replace: usable } });
      if (dryRun) setCheck(res);
      else {
        setDone(res);
        setCheck(null);
        void qc.invalidateQueries({ queryKey: ["store-products"] });
      }
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  function template() {
    if (!meta) return;
    const url = URL.createObjectURL(new Blob(["﻿" + meta.csvTemplate], { type: "text/csv;charset=utf-8" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "savon-store-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">1. Products (CSV)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          Use SAVON's template, or a shop export with <span className="font-mono">Name *, Price *, Special Price, Categories, In Stock, Base Image</span>. A product whose
          SKU is already here is updated with the columns the file has; others are added. The file is checked before anything changes.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".csv,text/csv"
            className="text-sm"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              setCheck(null);
              setDone(null);
              if (!file) return setCsv(null);
              const text = await file.text();
              setCsv({ name: file.name, text });
              void run(true, text);
            }}
          />
          <Button size="sm" variant="outline" onClick={template} disabled={!meta}>
            Download template
          </Button>
          {busy && <span className="text-muted-foreground">Working…</span>}
        </div>
        <div className="space-y-2 rounded-md border p-3">
          <div className="font-medium">Replace words in names and descriptions</div>
          {replace.map((r, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2">
              <input
                className="h-8 w-40 rounded-md border bg-background px-2"
                value={r.from}
                placeholder="Find"
                onChange={(e) => setReplace((all) => all.map((x, j) => (j === i ? { ...x, from: e.target.value } : x)))}
              />
              <span>→</span>
              <input
                className="h-8 w-40 rounded-md border bg-background px-2"
                value={r.to}
                placeholder="Replace with"
                onChange={(e) => setReplace((all) => all.map((x, j) => (j === i ? { ...x, to: e.target.value } : x)))}
              />
              <button type="button" className="text-muted-foreground underline" onClick={() => setReplace((all) => all.filter((_, j) => j !== i))}>
                Remove
              </button>
            </div>
          ))}
          <div className="flex gap-3">
            {replace.length < 10 && (
              <button type="button" className="underline" onClick={() => setReplace((all) => [...all, { from: "", to: "" }])}>
                Add a word
              </button>
            )}
            {csv && (
              <button type="button" className="underline" disabled={busy} onClick={() => void run(true)}>
                Check the file again
              </button>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Whole words, any capitals. Prices, SKUs and photo names are never changed.</p>
        </div>
        {check && (
          <div className="space-y-2 rounded-md border p-3">
            <p>
              <b>{csv?.name}</b>: {check.created} new, {check.updated} updated{check.errors.length ? `, ${check.errors.length} lines skipped` : ""}.
            </p>
            <Messages title="Skipped" items={check.errors} tone="text-destructive" />
            <Messages title="Notes" items={check.notes} tone="text-amber-700" />
            <Button size="sm" disabled={busy || check.created + check.updated === 0} onClick={() => run(false)}>
              Import {check.created + check.updated} products
            </Button>
          </div>
        )}
        {done && (
          <p className="rounded-md border border-emerald-300 bg-emerald-50 p-3 text-emerald-900">
            Imported: {done.created} added, {done.updated} updated. Now add their photos below.
          </p>
        )}
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}

function Messages({ title, items, tone }: { title: string; items: string[]; tone: string }) {
  const [all, setAll] = useState(false);
  if (items.length === 0) return null;
  const shown = all ? items : items.slice(0, 8);
  return (
    <div className={tone}>
      <div className="font-medium">{title}</div>
      <ul className="list-disc pl-5">
        {shown.map((m, i) => (
          <li key={i}>{m}</li>
        ))}
      </ul>
      {items.length > shown.length && (
        <button type="button" className="underline" onClick={() => setAll(true)}>
          Show all {items.length}
        </button>
      )}
    </div>
  );
}

type Match = { productId: number; entry: ZipEntry };

function PhotoStep() {
  const qc = useQueryClient();
  const [zip, setZip] = useState<File | null>(null);
  const [matches, setMatches] = useState<{ all: Match[]; fresh: Match[]; missing: number; entries: number } | null>(null);
  const [replace, setReplace] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; failed: string[]; bytes: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reading, setReading] = useState(false);
  const stop = useRef(false);
  const running = progress !== null && progress.done < progress.total && !stop.current;

  async function open(file: File) {
    setZip(file);
    setMatches(null);
    setProgress(null);
    setError(null);
    setReading(true);
    try {
      const [entries, keys] = await Promise.all([zipEntries(file), api<{ products: ProductKey[] }>("/ops/store/products/keys")]);
      const images = entries.filter((e) => /\.(jpe?g|png|webp)$/i.test(e.name));
      const byName = new Map(images.map((e) => [baseName(e.name), e]));
      const byStem = new Map(images.map((e) => [baseName(e.name).replace(/\.[a-z0-9]+$/, ""), e]));
      const all: Match[] = [];
      const fresh: Match[] = [];
      let missing = 0;
      for (const p of keys.products) {
        const sku = p.sku.toLowerCase();
        const entry = (p.imageFile && byName.get(p.imageFile.toLowerCase())) || byStem.get(sku) || byStem.get(`${sku}-1`);
        if (!entry) {
          if (!p.hasImage) missing += 1;
          continue;
        }
        all.push({ productId: p.id, entry });
        if (!p.hasImage) fresh.push({ productId: p.id, entry });
      }
      setMatches({ all, fresh, missing, entries: images.length });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setReading(false);
    }
  }

  async function upload() {
    if (!zip || !matches) return;
    const list = replace ? matches.all : matches.fresh;
    stop.current = false;
    const state = { done: 0, total: list.length, failed: [] as string[], bytes: 0 };
    setProgress({ ...state });
    let next = 0;
    const worker = async () => {
      while (!stop.current && next < list.length) {
        const m = list[next++]!;
        try {
          const img = await prepareImage(await zipRead(zip, m.entry));
          await api(`/ops/store/products/${m.productId}/image`, { method: "PUT", body: { data: img.data } });
          state.bytes += img.bytes;
        } catch (err) {
          state.failed.push(`${baseName(m.entry.name)}: ${errorText(err)}`);
        }
        state.done += 1;
        setProgress({ ...state, failed: [...state.failed] });
      }
    };
    await Promise.all(Array.from({ length: PARALLEL }, worker));
    setProgress({ ...state, failed: [...state.failed] });
    void qc.invalidateQueries({ queryKey: ["store-products"] });
  }

  const count = matches ? (replace ? matches.all.length : matches.fresh.length) : 0;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">2. Photos (ZIP)</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          A ZIP of JPEG, PNG or WebP photos, in any folder. Each is matched to the product whose CSV <span className="font-mono">image</span> /{" "}
          <span className="font-mono">Base Image</span> names it, or whose SKU is its file name. Photos are made smaller here before they are sent, so a large ZIP is fine.
        </p>
        <input type="file" accept=".zip,application/zip" className="text-sm" disabled={running} onChange={(e) => e.target.files?.[0] && open(e.target.files[0])} />
        {reading && <p className="text-muted-foreground">Reading the ZIP…</p>}
        {matches && (
          <div className="space-y-2 rounded-md border p-3">
            <p>
              {matches.entries} photos in the ZIP. {matches.fresh.length} match products without a photo
              {matches.all.length > matches.fresh.length ? `, ${matches.all.length - matches.fresh.length} match products that have one` : ""}.
              {matches.missing > 0 && ` ${matches.missing} products still have no photo in this ZIP.`}
            </p>
            {matches.all.length > matches.fresh.length && (
              <label className="flex items-center gap-2">
                <input type="checkbox" className="h-4 w-4" checked={replace} disabled={running} onChange={(e) => setReplace(e.target.checked)} />
                Also replace photos products already have
              </label>
            )}
            <div className="flex gap-2">
              <Button size="sm" disabled={running || count === 0} onClick={() => void upload()}>
                Upload {count} photos
              </Button>
              {running && (
                <Button size="sm" variant="outline" onClick={() => (stop.current = true)}>
                  Stop
                </Button>
              )}
            </div>
          </div>
        )}
        {progress && (
          <div className="space-y-2">
            <div className="h-2 overflow-hidden rounded bg-muted">
              <div className="h-2 bg-emerald-600 transition-all" style={{ width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 100}%` }} />
            </div>
            <p>
              {progress.done} of {progress.total} done ({(progress.bytes / 1024 / 1024).toFixed(1)} MB sent)
              {progress.failed.length ? `, ${progress.failed.length} failed` : ""}
              {progress.done === progress.total ? ". Finished." : stop.current ? ". Stopped." : "…"}
            </p>
            <Messages title="Failed" items={progress.failed} tone="text-destructive" />
          </div>
        )}
        {error && <p className="text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
